import React from 'react';
import AutoGrowTextarea from './AutoGrowTextarea';
import { useJobPhases, burnLabel } from './WorkPhaseChips';

// Condensed table editor for the generic sections — one line per entry, same
// bordered-cell look as LaborMatrix / EquipmentMatrix. Columns come straight
// from def.fields; replaces the old RowCard card-per-entry layout.

const CELL_BORDER = '1px solid #CBD5E1';

const thStyle = {
    border: CELL_BORDER,
    background: '#F1F5F9',
    padding: '6px 8px',
    fontSize: 11,
    fontWeight: 700,
    color: '#334155',
    textAlign: 'left',
    verticalAlign: 'bottom',
    whiteSpace: 'nowrap',
};

const tdStyle = { border: CELL_BORDER, padding: 0, verticalAlign: 'middle', background: '#fff' };

const cellInput = {
    width: '100%',
    border: 'none',
    outline: 'none',
    background: 'transparent',
    fontSize: 13,
    color: '#0F172A',
    padding: '7px 8px',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
};

const colStyle = (f) => {
    if (f.computed) return { width: 90 };
    switch (f.type) {
        case 'costCode': return { width: 140, minWidth: 120 };
        case 'multiline': return { minWidth: 240 };
        case 'text': return { minWidth: 130 };
        case 'number': return { width: 90 };
        case 'select': return { width: 130 };
        case 'bool': return { width: 60 };
        case 'time': return { width: 100 };
        default: return { minWidth: 120 };
    }
};

const SectionTable = ({ def, rows, onChange, onDelete, readOnly, phaseCodes }) => {
    // Burn Tool schedule intervals — shown under the cost code so the table
    // carries the same dates as the phase chips above it.
    const { intervals } = useJobPhases();
    const renderCell = (f, row, i) => {
        const val = row[f.name] ?? '';
        const set = (v) => onChange(i)(f.name, v);
        if (f.computed) {
            const v = f.computed(row);
            return (
                <div style={{ ...cellInput, fontWeight: 600, color: '#334155', textAlign: 'right' }}>
                    {v === '' || v == null ? '—' : v}
                </div>
            );
        }
        if (f.type === 'select' || f.type === 'bool') {
            const options = f.type === 'bool' ? ['Y', 'N'] : f.options || [];
            return (
                <select
                    value={val}
                    disabled={readOnly}
                    onChange={(e) => set(e.target.value)}
                    style={{ ...cellInput, appearance: 'auto', cursor: readOnly ? 'default' : 'pointer', color: val ? '#0F172A' : '#94A3B8' }}
                >
                    <option value="">—</option>
                    {options.map((o) => (
                        <option key={o} value={o}>{o}</option>
                    ))}
                </select>
            );
        }
        if (f.type === 'time') {
            return (
                <input
                    type="time"
                    value={val}
                    disabled={readOnly}
                    onChange={(e) => set(e.target.value)}
                    style={cellInput}
                />
            );
        }
        if (f.type === 'costCode') {
            const cc = row[f.name] && phaseCodes?.[row[f.name]];
            const bt = burnLabel(intervals, row[f.name]);
            return (
                <div>
                    <input
                        type="text"
                        value={val}
                        disabled={readOnly}
                        list="fr-costcodes"
                        placeholder="Search…"
                        onChange={(e) => set(e.target.value)}
                        style={cellInput}
                    />
                    {cc ? (
                        <div style={{ fontSize: 13, color: '#475569', padding: `0 8px ${bt ? 2 : 5}px` }}>{cc}</div>
                    ) : null}
                    {bt ? (
                        <div style={{ fontSize: 10, color: '#94A3B8', padding: '0 8px 5px', whiteSpace: 'nowrap' }}>{bt}</div>
                    ) : null}
                </div>
            );
        }
        if (f.type === 'multiline') {
            return (
                <AutoGrowTextarea
                    value={String(val)}
                    disabled={readOnly}
                    placeholder={f.placeholder || ''}
                    onChange={(e) => set(e.target.value)}
                    style={{ ...cellInput, display: 'block' }}
                />
            );
        }
        return (
            <input
                type="text"
                inputMode={f.type === 'number' ? 'decimal' : undefined}
                value={String(val)}
                disabled={readOnly}
                list={f.type === 'vendor' ? 'fr-vendors' : f.type === 'po' ? 'fr-pos' : undefined}
                placeholder={f.placeholder || ''}
                onChange={(e) => set(e.target.value)}
                style={{ ...cellInput, textAlign: f.type === 'number' ? 'right' : 'left' }}
            />
        );
    };

    return (
        <div style={{ overflowX: 'auto', marginBottom: 8 }}>
            <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                <thead>
                    <tr>
                        {def.fields.map((f) => (
                            <th key={f.name} style={{ ...thStyle, ...colStyle(f) }}>{f.label}</th>
                        ))}
                        {!readOnly && <th style={{ ...thStyle, minWidth: 30, width: 30 }} />}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row, i) => (
                        <tr key={i}>
                            {def.fields.map((f) => (
                                <td key={f.name} style={{ ...tdStyle, ...(f.computed ? { background: '#F8FAFC' } : null) }}>
                                    {renderCell(f, row, i)}
                                </td>
                            ))}
                            {!readOnly && (
                                <td style={{ ...tdStyle, textAlign: 'center' }}>
                                    <button
                                        onClick={onDelete(i)}
                                        title="Remove row"
                                        style={{ border: 'none', background: 'transparent', color: '#DC2626', cursor: 'pointer', fontSize: 14, lineHeight: 1, padding: '6px 8px' }}
                                    >×</button>
                                </td>
                            )}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

export default SectionTable;
